/**
 * Network Status Badge Component
 * Displays current network connectivity status during recording
 * Shows online/offline state with visual indicators
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiIcon, ExclamationTriangleIcon, CloudIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

interface NetworkStatusBadgeProps {
  isOnline: boolean;
  savedChunksCount?: number;
  lastSavedAt?: Date | null;
  isRecording?: boolean;
  className?: string;
  compact?: boolean;
}

export const NetworkStatusBadge: React.FC<NetworkStatusBadgeProps> = ({
  isOnline,
  savedChunksCount = 0,
  lastSavedAt,
  isRecording = false,
  className = '',
  compact = false,
}) => {
  const getTimeSinceLastSave = (): string | null => {
    if (!lastSavedAt) return null;

    const seconds = Math.floor((Date.now() - lastSavedAt.getTime()) / 1000);

    if (seconds < 10) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;

    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ago`;
  };

  if (compact) {
    return (
      <motion.div
        className={`flex items-center gap-1.5 ${className}`}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div
          className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`}
          animate={!isOnline ? { scale: [1, 1.3, 1], opacity: [1, 0.7, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />
        <span className={`text-xs font-medium ${isOnline ? 'text-emerald-600' : 'text-amber-600'}`}>
          {isOnline ? 'Online' : 'Offline'}
        </span>
      </motion.div>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={isOnline ? 'online' : 'offline'}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        transition={{ duration: 0.2 }}
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${
          isOnline
            ? 'bg-emerald-50 border border-emerald-200'
            : 'bg-amber-50 border border-amber-200'
        } ${className}`}
      >
        {/* Status indicator dot */}
        <motion.div
          className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500' : 'bg-amber-500'}`}
          animate={!isOnline ? { scale: [1, 1.3, 1], opacity: [1, 0.7, 1] } : {}}
          transition={{ repeat: Infinity, duration: 1.5 }}
        />

        {/* Icon */}
        {isOnline ? (
          <WifiIcon className="w-4 h-4 text-emerald-600" />
        ) : (
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600" />
        )}

        {/* Status text */}
        <span className={`text-xs font-medium ${isOnline ? 'text-emerald-700' : 'text-amber-700'}`}>
          {isOnline ? 'Connected' : 'Offline'}
        </span>

        {/* Additional info when recording */}
        {isRecording && (
          <>
            <span className="text-slate-300">|</span>

            {/* Chunk save indicator */}
            {savedChunksCount > 0 && (
              <motion.div
                className="flex items-center gap-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <ShieldCheckIcon className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs text-blue-600">
                  {savedChunksCount} saved
                </span>
              </motion.div>
            )}

            {/* Last save time */}
            {lastSavedAt && (
              <span className="text-xs text-slate-400">
                {getTimeSinceLastSave()}
              </span>
            )}
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

/**
 * Protection Status Badge
 * Shows comprehensive protection status including network and auto-save
 */
interface ProtectionStatusBadgeProps {
  isOnline: boolean;
  isRecording: boolean;
  savedChunksCount: number;
  isAutoSaving: boolean;
  className?: string;
}

export const ProtectionStatusBadge: React.FC<ProtectionStatusBadgeProps> = ({
  isOnline,
  isRecording,
  savedChunksCount,
  isAutoSaving,
  className = '',
}) => {
  const getStatusColor = () => {
    if (!isOnline && isRecording) return 'amber'; // Offline while recording - warning
    if (isRecording && savedChunksCount > 0) return 'emerald'; // Recording with saves - good
    if (isRecording) return 'blue'; // Recording but no saves yet
    return 'slate'; // Idle
  };

  const color = getStatusColor();

  const colorClasses = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
  };

  const dotColors = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    blue: 'bg-blue-500',
    slate: 'bg-slate-400',
  };

  return (
    <motion.div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${colorClasses[color]} ${className}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      {/* Animated status dot */}
      <motion.div
        className={`w-2 h-2 rounded-full ${dotColors[color]}`}
        animate={isRecording ? { scale: [1, 1.2, 1] } : {}}
        transition={{ repeat: Infinity, duration: 2 }}
      />

      {/* Protection icon */}
      {isAutoSaving ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 2, ease: 'linear' }}
        >
          <CloudIcon className="w-4 h-4" />
        </motion.div>
      ) : (
        <ShieldCheckIcon className="w-4 h-4" />
      )}

      {/* Status text */}
      <span className="text-xs font-medium">
        {!isOnline && isRecording
          ? 'Saving locally'
          : isAutoSaving
            ? 'Auto-saving'
            : savedChunksCount > 0
              ? `${savedChunksCount} checkpoints`
              : 'Protected'}
      </span>

      {/* Network warning icon when offline */}
      {!isOnline && (
        <ExclamationTriangleIcon className="w-3.5 h-3.5 text-amber-500 ml-1" />
      )}
    </motion.div>
  );
};

export default NetworkStatusBadge;
